/**
 * FieldLock.ts — SKCATALOG-CC-002 CR-2 (P02) brand standard, console side.
 *
 * The API refuses to LOCK a price-kind field (400 `price_field_not_lockable`),
 * because local price flexibility is a deliberate design decision: a Chennai
 * branch and a Bandra branch charge different amounts for the same service, and
 * that is the product working. SKCATALOG-DB-001 §2 says the console must not even
 * OFFER the toggle for those fields — an offered control that always errors is
 * worse than no control, because the operator reads the failure as a bug.
 *
 * This predicate mirrors the backend's (src/services/fieldLock.ts) exactly. It is
 * duplicated rather than fetched because it is a design rule, not tenant data,
 * and a round trip to decide whether to render a toggle would make the field list
 * flicker. If the backend list changes, change both — the API stays the referee
 * either way, so a drift here degrades to a refused request, not a wrong write.
 */

import type { FieldDefinition, FieldType } from '../schemas/catalog/itemType';

/** Field keys that are price-kind whatever their declared type. */
const PRICE_FIELD_KEYS = new Set([
  'base_price',
  'original_price',
  'price',
  'mrp',
  'sale_price',
  'addon_price',
]);

/** Declared types that carry money. */
const PRICE_FIELD_TYPES = new Set<FieldType>(['CURRENCY']);

/** Is this field money — i.e. must it stay locally flexible? */
export function isPriceKindField(field: { fieldKey: string; fieldType: FieldType | string }): boolean {
  return (
    PRICE_FIELD_TYPES.has(String(field.fieldType).toUpperCase() as FieldType) ||
    PRICE_FIELD_KEYS.has(field.fieldKey.toLowerCase())
  );
}

/** Should the brand-scope editor show a lock toggle for this field? */
export function isLockableField(field: { fieldKey: string; fieldType: FieldType | string }): boolean {
  return !isPriceKindField(field);
}

/**
 * Is this field read-only for the CURRENT editing scope?
 *
 * Locked fields are read-only at LOCATION scope only — the brand owns the locked
 * value and may change it, so a brand-scope editor (locationId absent) still
 * edits freely. Getting this backwards would freeze the field permanently at
 * whatever it said when someone clicked the toggle.
 */
export function isReadOnlyInScope(
  field: Pick<FieldDefinition, 'isLocked'>,
  locationId?: string | null,
): boolean {
  return !!field.isLocked && !!locationId;
}

/** The helper text a locked field shows at location scope (DB-001 §2). */
export const LOCKED_HELPER_TEXT = 'Set by brand — locked.';
